import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PRIORIDADES_TAREFA,
  type OrigemTarefa,
  type PrioridadeTarefa,
  type StatusTarefa,
} from '@reducee/contracts';
import { useState, type FormEvent } from 'react';

import { CabecalhoDePagina, Indicador, Pilula, data } from '../componentes/Superficie.tsx';
import { chamarApi, ErroDaApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CampoSelecao, CampoTexto } from '../componentes/Campo.tsx';

type Tarefa = {
  id: string;
  titulo: string;
  descricao: string | null;
  prazo: string | null;
  prioridade: PrioridadeTarefa;
  status: StatusTarefa;
  origem: OrigemTarefa;
  atrasada: boolean;
  contato: { id: string; nome: string } | null;
  oportunidade: { id: string; produto: string } | null;
  responsavel: { id: string; nome: string };
};

const FILTROS = [
  { valor: 'ABERTAS', rotulo: 'Abertas' },
  { valor: 'HOJE', rotulo: 'Vencendo hoje' },
  { valor: 'ATRASADAS', rotulo: 'Atrasadas' },
  { valor: 'TODAS', rotulo: 'Todas' },
] as const;

const ROTULO_PRIORIDADE: Record<PrioridadeTarefa, string> = {
  BAIXA: 'Baixa',
  MEDIA: 'Média',
  ALTA: 'Alta',
  URGENTE: 'Urgente',
};

const ROTULO_STATUS: Record<StatusTarefa, string> = {
  A_FAZER: 'A fazer',
  EM_ANDAMENTO: 'Em andamento',
  CONCLUIDA: 'Concluída',
};

const ROTULO_ORIGEM: Record<OrigemTarefa, string> = {
  MANUAL: 'Manual',
  CADENCIA: 'Cadência',
  REATIVACAO: 'Reativação',
  PROXIMO_PASSO: 'Próximo passo',
  IMPORTACAO: 'Importação',
  META_ADS: 'Meta Ads',
};

/** Trabalho com dono e prazo: próximos passos e pendências automáticas. */
export function PaginaTarefas(): React.JSX.Element {
  const clienteDeConsultas = useQueryClient();
  const [filtro, setFiltro] = useState<(typeof FILTROS)[number]['valor']>('ABERTAS');
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const { usuaria } = useSessao();
  const hojeEmTexto = new Date().toISOString().slice(0, 10);

  // Os indicadores contam todas as tarefas, não as do filtro escolhido.
  const consultaDeTodas = useQuery({
    queryKey: ['tarefas', 'TODAS'],
    queryFn: () => chamarApi<Tarefa[]>('/tarefas?filtro=TODAS'),
  });

  const todas = consultaDeTodas.data ?? [];

  const consulta = useQuery({
    queryKey: ['tarefas', filtro],
    queryFn: () => chamarApi<Tarefa[]>(`/tarefas?filtro=${filtro}`),
  });

  const atualizar = async (): Promise<void> => {
    setErro(null);
    await clienteDeConsultas.invalidateQueries({ queryKey: ['tarefas'] });
  };

  const tratarErro = (causa: unknown): void => {
    setErro(causa instanceof ErroDaApi ? causa.message : 'Não foi possível concluir a operação.');
  };

  const criar = useMutation({
    mutationFn: (corpo: Record<string, unknown>) =>
      chamarApi<Tarefa>('/tarefas', { metodo: 'POST', corpo }),
    onSuccess: async () => {
      setFormularioAberto(false);
      await atualizar();
    },
    onError: tratarErro,
  });

  const mudarStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: StatusTarefa }) =>
      chamarApi<Tarefa>(`/tarefas/${id}`, { metodo: 'PATCH', corpo: { status } }),
    onSuccess: atualizar,
    onError: tratarErro,
  });

  function aoCriar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    const campos = new FormData(evento.currentTarget);
    setErro(null);
    criar.mutate({
      titulo: String(campos.get('titulo') ?? ''),
      prazo: String(campos.get('prazo') ?? '') || undefined,
      prioridade: String(campos.get('prioridade') ?? 'MEDIA'),
    });
  }

  return (
    <section>
      <CabecalhoDePagina
        area="Operação"
        titulo="Tarefas"
        descricao="Atividades ligadas a clientes, contratos, movimentações e renovações."
        acoes={
          <button
            type="button"
            onClick={() => setFormularioAberto((aberto) => !aberto)}
            className="rounded-[10px] bg-marinho px-3.25 py-2.5 text-base font-[750] text-white transition hover:bg-marinho-claro"
          >
            + Nova tarefa
          </button>
        }
      />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <Indicador
          rotulo="Abertas"
          valor={String(todas.filter((tarefa) => tarefa.status !== 'CONCLUIDA').length)}
        />
        <Indicador
          rotulo="Vencendo hoje"
          valor={String(
            todas.filter(
              (tarefa) =>
                tarefa.status !== 'CONCLUIDA' && tarefa.prazo?.slice(0, 10) === hojeEmTexto,
            ).length,
          )}
        />
        <Indicador
          rotulo="Atrasadas"
          valor={String(todas.filter((tarefa) => tarefa.atrasada).length)}
          severidade="erro"
        />
        <Indicador
          rotulo="Concluídas"
          valor={String(todas.filter((tarefa) => tarefa.status === 'CONCLUIDA').length)}
          severidade="sucesso"
        />
        <Indicador
          rotulo="Minhas tarefas"
          valor={String(
            todas.filter(
              (tarefa) => tarefa.status !== 'CONCLUIDA' && tarefa.responsavel.id === usuaria?.id,
            ).length,
          )}
        />
      </div>

      <div className="mt-3 flex gap-2">
        {FILTROS.map((opcao) => (
          <button
            key={opcao.valor}
            type="button"
            onClick={() => setFiltro(opcao.valor)}
            className={`rounded-lg px-3 py-1.5 text-sm transition ${
              filtro === opcao.valor
                ? 'bg-marinho font-medium text-white'
                : 'bg-superficie ring-1 ring-linha hover:bg-marinho-suave'
            }`}
          >
            {opcao.rotulo}
          </button>
        ))}
      </div>

      {erro ? (
        <p role="alert" className="mt-4 rounded-lg bg-erro/10 px-3 py-2 text-sm text-erro">
          {erro}
        </p>
      ) : null}

      {formularioAberto ? (
        <form
          onSubmit={aoCriar}
          className="mt-4 grid gap-4 rounded-xl bg-superficie p-5 ring-1 ring-linha md:grid-cols-4"
        >
          <CampoTexto rotulo="Título" name="titulo" required className="md:col-span-2" />
          <CampoTexto rotulo="Prazo" name="prazo" type="date" />
          <CampoSelecao rotulo="Prioridade" name="prioridade" defaultValue="MEDIA">
            {PRIORIDADES_TAREFA.map((prioridade) => (
              <option key={prioridade} value={prioridade}>
                {ROTULO_PRIORIDADE[prioridade]}
              </option>
            ))}
          </CampoSelecao>
          <div className="md:col-span-4">
            <button
              type="submit"
              disabled={criar.isPending}
              className="rounded-lg bg-marinho px-4 py-2 text-sm font-medium text-white transition hover:bg-marinho-claro disabled:opacity-60"
            >
              Salvar tarefa
            </button>
          </div>
        </form>
      ) : null}

      {/* Quadro de três colunas, como no protótipo: a tarefa muda de coluna
        * conforme anda, e o que está parado fica visível de longe. */}
      <div className="mt-3 grid gap-2.5 lg:grid-cols-3">
        {(
          [
            { status: 'A_FAZER', titulo: 'A fazer', descricao: 'Pendentes e atrasadas' },
            { status: 'EM_ANDAMENTO', titulo: 'Em andamento', descricao: 'Atividades em execução' },
            { status: 'CONCLUIDA', titulo: 'Concluídas', descricao: 'Histórico recente' },
          ] as const
        ).map((coluna) => {
          const daColuna = (consulta.data ?? []).filter((tarefa) => tarefa.status === coluna.status);

          return (
            <div
              key={coluna.status}
              data-teste={`coluna-tarefas-${coluna.status}`}
              className="min-h-[220px] rounded-[13px] bg-[#eef1f6] p-2.5"
            >
              <div className="text-base font-extrabold">{coluna.titulo}</div>
              <div className="mb-3 text-xs text-texto-suave">{coluna.descricao}</div>

              {daColuna.length === 0 ? (
                <div className="py-4.5 text-xs text-texto-suave">
                  {coluna.status === 'CONCLUIDA' ? 'Sem tarefas concluídas.' : 'Sem tarefas.'}
                </div>
              ) : (
                daColuna.map((tarefa) => (
                  <div
                    key={tarefa.id}
                    data-teste="tarefa"
                    className="mb-2 rounded-[11px] border border-linha bg-superficie p-2.5"
                  >
                    <b className="block text-[11px]">{tarefa.titulo}</b>
                    <p className="text-[10px] text-texto-suave">
                      {tarefa.contato?.nome ?? 'Sem cliente'} · {tarefa.responsavel.nome} ·{' '}
                      {data(tarefa.prazo)} · {ROTULO_PRIORIDADE[tarefa.prioridade]}
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <Pilula tom={tarefa.atrasada ? 'erro' : 'neutro'}>
                        {ROTULO_STATUS[tarefa.status]}
                      </Pilula>
                      {tarefa.status === 'A_FAZER' ? (
                        <button
                          type="button"
                          onClick={() =>
                            mudarStatus.mutate({ id: tarefa.id, status: 'EM_ANDAMENTO' })
                          }
                          className="rounded-lg border border-linha bg-superficie px-2 py-1.5 text-[10px] font-bold transition hover:bg-marinho-suave"
                        >
                          Iniciar
                        </button>
                      ) : null}
                      {tarefa.status !== 'CONCLUIDA' ? (
                        <button
                          type="button"
                          onClick={() => mudarStatus.mutate({ id: tarefa.id, status: 'CONCLUIDA' })}
                          className="rounded-lg border border-linha bg-superficie px-2 py-1.5 text-[10px] font-bold transition hover:bg-marinho-suave"
                        >
                          Concluir
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })}
      </div>

      {consulta.isLoading ? <p className="mt-3 text-texto-suave">Carregando…</p> : null}
    </section>
  );
}
